import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { EMAIL } from "@/config/constants";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AutosizeTextarea } from "@/components/ui/autosize-textarea";
import { LoadingButton } from "@/components/ui/loading-button";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useUser } from "@/hooks/useUser";
import { useNotification } from "@/hooks/useNotification";
import { useTranslation } from "@/hooks/useTranslation";
import { submitContact } from "@/lib/submit-contact";

// We'll create the schema inside the component to access translations
const createContactFormSchema = (t: (key: string) => string) =>
  z.object({
    description: z.string().min(5, t("contact.message_min_length")),
    email: z.string().email(t("contact.email_invalid")).optional(),
  });

type ContactFormValues = {
  description: string;
  email?: string;
};

type ErrorMessage = {
  title: string;
  description: string;
};

interface ContactFormProps {
  onSuccess?: () => void;
}

export function ContactForm({ onSuccess }: ContactFormProps) {
  const { showNotification } = useNotification();
  const { isAuthenticated, user, userId } = useUser();
  const { t } = useTranslation();

  const contactFormSchema = createContactFormSchema(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      description: "",
      email: "",
    },
  });

  // Update form values when user data becomes available
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      form.setValue("email", user.email);
    }
  }, [isAuthenticated, user?.email, form]);

  const onSubmit = async (values: ContactFormValues) => {
    try {
      await submitContact({
        data: {
          userId: userId || undefined,
          email: isAuthenticated ? (user?.email as string) : (values.email as string),
          description: values.description,
        },
      });

      showNotification({
        type: "success",
        message: t("contact.success_title"),
        description: t("contact.success_description"),
      });

      // Reset form but preserve email
      const currentEmail = form.getValues("email");
      form.reset({
        description: "",
        email: currentEmail,
      });

      onSuccess?.();
    } catch (error) {
      console.error("Error in contact form submission:", error);

      showNotification({
        type: "error",
        message: t("contact.error_title"),
        description: t("contact.error_description"),
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            {(() => {
              try {
                const errorMessage: ErrorMessage = JSON.parse(
                  form.formState.errors.root.message as string,
                );
                return (
                  <>
                    <AlertTitle>{errorMessage.title}</AlertTitle>
                    <AlertDescription>{errorMessage.description}</AlertDescription>
                  </>
                );
              } catch {
                return (
                  <>
                    <AlertTitle>{t("contact.error_title")}</AlertTitle>
                    <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
                  </>
                );
              }
            })()}
          </Alert>
        )}

        <p className="text-sm text-muted-foreground">
          {t("contact.feedback_description")}{" "}
          <a
            href={`mailto:${EMAIL}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {EMAIL}
          </a>
        </p>

        {!isAuthenticated && !user?.email && (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("contact.your_email")} <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t("contact.email_placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("contact.message")} <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <AutosizeTextarea
                  placeholder={t("contact.message_placeholder")}
                  {...field}
                  minHeight={60}
                  maxHeight={300}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          <LoadingButton
            data-umami-event="contact-form-submit"
            type="submit"
            loading={false}
            disabled={form.formState.isSubmitting}
            className="w-full"
          >
            {t("contact.submit_message")}
          </LoadingButton>
        </div>
      </form>
    </Form>
  );
}
